import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useUserTracking } from '@/hooks/useUserTracking';
import { useDataLayer } from '@/hooks/useDataLayer';

const loadFormUtils = () => Promise.all([
  import('@/utils/phoneUtils'),
  import('@/utils/whatsappUtils'),
  import('@/utils/webhookUtils'),
  import('@/data/countries'),
]);

let formUtilsCache: Awaited<ReturnType<typeof loadFormUtils>> | null = null;
const getFormUtils = async () => {
  if (!formUtilsCache) formUtilsCache = await loadFormUtils();
  return formUtilsCache;
};

const initialFormState = {
  name: '', email: '', phone: '', country: 'Brasil', countryCode: '+55',
  city: '', location: '', procedure: '', customProcedure: '', message: ''
};

interface BookingContextType {
  isWhatsAppOpen: boolean;
  setIsWhatsAppOpen: (v: boolean) => void;
  isSuccessPopupOpen: boolean;
  countdown: number;
  whatsAppFormData: typeof initialFormState;
  setWhatsAppFormData: React.Dispatch<React.SetStateAction<typeof initialFormState>>;
  hasStartedWhatsAppForm: boolean;
  handleWhatsAppOpen: (procedure?: string) => void;
  handleWhatsAppFormSubmit: (e: React.FormEvent) => Promise<void>;
  handleWhatsAppPhoneChange: (value: string) => Promise<void>;
  handleCountryChange: (countryName: string, formType: 'main' | 'whatsapp') => Promise<void>;
  handleWhatsAppFormFieldChange: (field: string, value: string) => void;
  handleSuccessRedirect: () => void;
  handleSuccessClose: () => void;
}

const BookingContext = createContext<BookingContextType | null>(null);

export const useBooking = () => {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error('useBooking must be used within BookingProvider');
  return ctx;
};

export const BookingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isWhatsAppOpen, setIsWhatsAppOpen] = useState(false);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [whatsAppFormData, setWhatsAppFormData] = useState({ ...initialFormState });
  const [hasStartedWhatsAppForm, setHasStartedWhatsAppForm] = useState(false);
  const trackingData = useUserTracking();
  const dataLayer = useDataLayer();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isSuccessPopupOpen && countdown > 0) {
      timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [isSuccessPopupOpen, countdown]);

  const handleWhatsAppOpen = useCallback((procedure?: string) => {
    if (procedure) {
      setWhatsAppFormData(prev => ({ ...prev, procedure }));
    }
    setIsWhatsAppOpen(true);
    dataLayer.trackWhatsAppOpen();
    getFormUtils();
  }, [dataLayer]);

  const handleWhatsAppPhoneChange = useCallback(async (value: string) => {
    const [phoneUtils] = await getFormUtils();
    setWhatsAppFormData(prev => ({
      ...prev,
      phone: phoneUtils.applyPhoneMask(value, prev.country || 'Brasil')
    }));
    if (!hasStartedWhatsAppForm && value.length > 0) {
      setHasStartedWhatsAppForm(true);
      dataLayer.trackFormStart('whatsapp');
    }
  }, [hasStartedWhatsAppForm, dataLayer]);

  const handleCountryChange = useCallback(async (countryName: string, formType: 'main' | 'whatsapp') => {
    if (formType !== 'whatsapp') return;
    const [,, , countriesModule] = await getFormUtils();
    const selectedCountry = countriesModule.countries.find((c: any) => c.name === countryName);
    const countryCode = selectedCountry ? selectedCountry.code : '';
    setWhatsAppFormData(prev => ({ ...prev, country: countryName, countryCode, phone: '' }));
    if (!hasStartedWhatsAppForm) {
      setHasStartedWhatsAppForm(true);
      dataLayer.trackFormStart('whatsapp');
    }
  }, [hasStartedWhatsAppForm, dataLayer]);

  const handleWhatsAppFormFieldChange = useCallback((field: string, value: string) => {
    setWhatsAppFormData(prev => ({ ...prev, [field]: value }));
    if (!hasStartedWhatsAppForm && value.length > 0) {
      setHasStartedWhatsAppForm(true);
      dataLayer.trackFormStart('whatsapp');
    }
  }, [hasStartedWhatsAppForm, dataLayer]);

  const handleWhatsAppFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!whatsAppFormData.name?.trim()) {
      toast.error("Nome obrigatório", { description: "Por favor, preencha seu nome." }); return;
    }
    if (!whatsAppFormData.email?.trim()) {
      toast.error("Email obrigatório", { description: "Por favor, preencha seu email." }); return;
    }
    if (!whatsAppFormData.phone?.trim()) {
      toast.error("Telefone obrigatório", { description: "Por favor, preencha seu telefone." }); return;
    }
    if (!whatsAppFormData.country?.trim()) {
      toast.error("País obrigatório", { description: "Por favor, selecione seu país." }); return;
    }
    if (whatsAppFormData.procedure === 'Outro' && !whatsAppFormData.customProcedure?.trim()) {
      toast.error("Procedimento obrigatório", { description: "Por favor, especifique o procedimento desejado." }); return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(whatsAppFormData.email)) {
      toast.error("Email inválido", { description: "Por favor, insira um email válido." }); return;
    }

    dataLayer.trackFormSubmit('whatsapp', whatsAppFormData);

    const [phoneUtils, whatsappUtils, webhookUtils] = await getFormUtils();

    const formDataToSend = {
      name: whatsAppFormData.name.trim(),
      email: whatsAppFormData.email.trim(),
      phone: phoneUtils.extractPhoneNumbers(whatsAppFormData.phone),
      country: whatsAppFormData.country || '',
      countryCode: whatsAppFormData.countryCode || '',
      city: whatsAppFormData.city || '',
      location: whatsAppFormData.location || '',
      procedure: whatsAppFormData.procedure || '',
      customProcedure: whatsAppFormData.procedure === 'Outro' ? (whatsAppFormData.customProcedure || '') : '',
      message: whatsAppFormData.message || '',
      origem: trackingData.origem || 'direto',
      midia: trackingData.midia || 'direto',
      url: trackingData.url || window.location.href,
      formulario: 'whatsapp'
    };

    const webhookResult = await webhookUtils.sendToWebhook(formDataToSend);
    if (webhookResult.success) dataLayer.trackLeadGenerated(formDataToSend);

    const url = whatsappUtils.createWhatsAppUrl(whatsAppFormData, trackingData);
    dataLayer.trackWhatsAppRedirect(whatsAppFormData.procedure === 'Outro' ? whatsAppFormData.customProcedure : whatsAppFormData.procedure);

    setWhatsappUrl(url);
    setCountdown(3);
    setIsSuccessPopupOpen(true);
    setIsWhatsAppOpen(false);
    setWhatsAppFormData({ ...initialFormState });
    setHasStartedWhatsAppForm(false);
  }, [whatsAppFormData, trackingData, dataLayer, toast]);

  const handleSuccessRedirect = useCallback(() => {
    if (whatsappUrl) window.location.href = whatsappUrl;
  }, [whatsappUrl]);

  const handleSuccessClose = useCallback(() => {
    setIsSuccessPopupOpen(false);
    setCountdown(3);
    if (whatsappUrl) window.location.href = whatsappUrl;
  }, [whatsappUrl]);

  return (
    <BookingContext.Provider value={{
      isWhatsAppOpen, setIsWhatsAppOpen,
      isSuccessPopupOpen, countdown,
      whatsAppFormData, setWhatsAppFormData,
      hasStartedWhatsAppForm,
      handleWhatsAppOpen,
      handleWhatsAppFormSubmit,
      handleWhatsAppPhoneChange,
      handleCountryChange,
      handleWhatsAppFormFieldChange,
      handleSuccessRedirect,
      handleSuccessClose,
    }}>
      {children}
    </BookingContext.Provider>
  );
};
